<template>
  <div v-if="shouldDisplayStores">
    <RevButtonBase
      target="_blank"
      :to="getStoreUrl(finalPlayStoreURL)"
      :tracking="{
        name: finalPlayTrackingName,
        zone: trackingZone,
      }"
    >
      <RevIllustration
        :alt="i18n(translations.googlePlayImageAlt)"
        class="inline-block h-[4rem]"
        :height="40"
        :src="`/img/socials/${market.countryCode}/google-play.svg`"
        :width="135"
      />
    </RevButtonBase>

    <RevButtonBase
      target="_blank"
      :to="getStoreUrl(finalAppleStoreURL)"
      :tracking="{
        name: finalAppleTrackingName,
        zone: trackingZone,
      }"
    >
      <RevIllustration
        :alt="i18n(translations.appStoreImageAlt)"
        class="ml-4 inline-block h-[4rem]"
        :height="40"
        :src="`/img/socials/${market.countryCode}/apple-store.svg`"
        :width="127"
      />
    </RevButtonBase>
  </div>
</template>

<script lang="ts" setup>
import { useRuntimeConfig } from '#imports'
import { computed } from 'vue'

import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { useMarketplace } from '@backmarket/nuxt-module-marketplace/useMarketplace'
import { RevButtonBase } from '@ds/components/ButtonBase'
import { RevIllustration } from '@ds/components/Illustration'

import { STORE_COUNTRIES } from './AppStoreButtons.constant'
import translations from './AppStoreButtons.translations'

// Note that before registering new URLs to this configuration, you should
// also download and store associated assets in `public/img/social`.

// assets may be found here: https://www.figma.com/file/jC2mtHRgj5YEWRLSQYfKHq/04.-Assets?node-id=1%3A852

type AppStoreButtonsProps = {
  campaign?: string
  trackingZone: string
  appleStoreTrackingName?: string
  playStoreTrackingName?: string
  appleStoreUrl?: string
  playStoreUrl?: string
  showInAllCountries?: boolean
}

const props = defineProps<AppStoreButtonsProps>()
const i18n = useI18n()
const { market } = useMarketplace()
const runtimeConfig = useRuntimeConfig()

const { APPSFLYER_APPLE_STORE_URL, APPSFLYER_PLAY_STORE_URL } =
  runtimeConfig.public

const finalAppleStoreURL = computed(
  () => props.appleStoreUrl || APPSFLYER_APPLE_STORE_URL,
)
const finalPlayStoreURL = computed(
  () => props.playStoreUrl || APPSFLYER_PLAY_STORE_URL,
)
const finalAppleTrackingName = computed(
  () => props.appleStoreTrackingName || 'redirect_app_store',
)
const finalPlayTrackingName = computed(
  () => props.playStoreTrackingName || 'redirect_google_play',
)

function getStoreUrl(storeBaseUrl: string) {
  return `${storeBaseUrl}?&af_ad=${market.countryCode.toLowerCase()}&af_adset=${
    props.campaign
  }`
}

const shouldDisplayStores = computed(() => {
  if (props.showInAllCountries) return true

  return STORE_COUNTRIES.includes(market.countryCode)
})
</script>
